import type { IntlShape } from 'react-intl'

interface FormatTimeRemainingOptions {
  variant?: 'duration' | 'left-suffix' | 'ends-in-prefix'
}

/**
 * Formats time remaining until a date using calendar months and days.
 * Accounts for actual month lengths (28/30/31 days) and localises the
 * plural forms via react-intl.
 *
 * Examples (English):
 *   - "3 months and 9 days left"
 *   - "1 month left"
 *   - "15 days left"
 *   - undefined if already expired
 */
export function formatTimeRemaining(expiresAt: string, intl: IntlShape, options: FormatTimeRemainingOptions = {}): string | undefined {
  const now = new Date()
  const expiry = new Date(expiresAt)
  const variant = options.variant ?? 'left-suffix'

  if (expiry <= now) return undefined

  let months = 0
  const cursor = new Date(now)

  // Count full calendar months
  while (true) {
    const next = new Date(cursor)
    next.setMonth(next.getMonth() + 1)
    if (next > expiry) break
    cursor.setMonth(cursor.getMonth() + 1)
    months++
  }

  // Remaining days after full months
  const diffMs = expiry.getTime() - cursor.getTime()
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24))

  if (months === 0) {
    switch (variant) {
      case 'ends-in-prefix':
        return intl.formatMessage(
          {
            id: 'caZkrFnZdVifjO8zbMtM',
            defaultMessage: 'Ends in {days, plural, one {# day} other {# days}}',
            description: 'Time remaining until expiry when less than one full month with an ends-in prefix, e.g. "Ends in 15 days"',
          },
          { days },
        )

      case 'duration':
        return intl.formatMessage(
          {
            id: '3eu5WZfbC2c9Y57C9KSM',
            defaultMessage: '{days, plural, one {# day} other {# days}}',
            description: 'Time remaining until expiry when less than one full month without a suffix, e.g. "15 days"',
          },
          { days },
        )

      case 'left-suffix':
        return intl.formatMessage(
          {
            id: 'qai4g4paVC8cREHYYaRT',
            defaultMessage: '{days, plural, one {# day} other {# days}} left',
            description: 'Time remaining until expiry when less than one full month, e.g. "15 days left"',
          },
          { days },
        )

      default:
        variant satisfies never
    }
  }

  if (days === 0) {
    switch (variant) {
      case 'ends-in-prefix':
        return intl.formatMessage(
          {
            id: 'tbTa1t5U43GAWKbYUBHg',
            defaultMessage: 'Ends in {months, plural, one {# month} other {# months}}',
            description: 'Time remaining until expiry when exactly a whole number of months with an ends-in prefix, e.g. "Ends in 3 months"',
          },
          { months },
        )

      case 'duration':
        return intl.formatMessage(
          {
            id: 'O1L7ePWsCvZmDeVJOO9I',
            defaultMessage: '{months, plural, one {# month} other {# months}}',
            description: 'Time remaining until expiry when exactly a whole number of months without a suffix, e.g. "3 months"',
          },
          { months },
        )

      case 'left-suffix':
        return intl.formatMessage(
          {
            id: 'ZzSJB_A3tRDcmVqgfkWU',
            defaultMessage: '{months, plural, one {# month} other {# months}} left',
            description: 'Time remaining until expiry when exactly a whole number of months, e.g. "3 months left"',
          },
          { months },
        )

      default:
        variant satisfies never
    }
  }

  switch (variant) {
    case 'ends-in-prefix':
      return intl.formatMessage(
        {
          id: 'S27kc_IZljP0TNuXJbgK',
          defaultMessage: 'Ends in {months, plural, one {# month} other {# months}} and {days, plural, one {# day} other {# days}}',
          description: 'Time remaining until expiry with both months and days with an ends-in prefix, e.g. "Ends in 3 months and 9 days"',
        },
        { months, days },
      )

    case 'duration':
      return intl.formatMessage(
        {
          id: 'VZ7EBfIKJwlcT-Jo_Chc',
          defaultMessage: '{months, plural, one {# month} other {# months}} and {days, plural, one {# day} other {# days}}',
          description: 'Time remaining until expiry with both months and days without a suffix, e.g. "3 months and 9 days"',
        },
        { months, days },
      )

    case 'left-suffix':
      return intl.formatMessage(
        {
          id: 'xg0GEbRWAy0CQ49OhWuX',
          defaultMessage: '{months, plural, one {# month} other {# months}} and {days, plural, one {# day} other {# days}} left',
          description: 'Time remaining until expiry with both months and days, e.g. "3 months and 9 days left"',
        },
        { months, days },
      )

    default:
      variant satisfies never
  }
}
